import { t } from 'i18next';
import _ from 'lodash';
import { formatMsLong } from '../frontend/libs/utils';
import { safeParseIntOrDefault } from './utils';
import { RecruitmentActivityType, RecruitmentEntityType, RecruitmentScheduleDay } from '../types/recruitmentTypes';

export const TOTAL_MINUTES_IN_DAY = 24 * 60;

export const DAYS = [
  { id: 0, slug: 'sunday' },
  { id: 1, slug: 'monday' },
  { id: 2, slug: 'tuesday' },
  { id: 3, slug: 'wednesday' },
  { id: 4, slug: 'thursday' },
  { id: 5, slug: 'friday' },
  { id: 6, slug: 'saturday' }
];

export const DAYS_BY_ID = _.keyBy(DAYS, 'id');

export const getNextDay = (day: number) => {
  return (day + 1) % 7;
};

const getPreviousDay = (day: number) => {
  return (day - 1 + 7) % 7;
};

export const adjustScheduleRanges = ({
  offset,
  day,
  startTimeMinutes,
  endTimeMinutes
}: {
  offset: number;
  day: number;
  startTimeMinutes?: string | number;
  endTimeMinutes?: string | number;
}): { day: number; start: number; end: number }[] => {
  const start = safeParseIntOrDefault(startTimeMinutes, 0);
  let end = safeParseIntOrDefault(endTimeMinutes, 0);

  if (end === 0) {
    end = TOTAL_MINUTES_IN_DAY;
  }

  // Special case: when start equals end (and both are not 0), treat as spanning to next day
  const spansToNextDay = start === end && start !== 0;
  const crossesMidnight = start > end && end !== TOTAL_MINUTES_IN_DAY;

  const offsetValue = -offset;
  let startWithOffset = start + offsetValue;
  let endWithOffset = end + offsetValue;

  let startDay = day;
  if (startWithOffset < 0) {
    startWithOffset += TOTAL_MINUTES_IN_DAY;
    startDay = getPreviousDay(day);
  } else if (startWithOffset >= TOTAL_MINUTES_IN_DAY) {
    startWithOffset -= TOTAL_MINUTES_IN_DAY;
    startDay = getNextDay(day);
  }

  let endDay = day;
  if (endWithOffset <= 0) {
    endWithOffset += TOTAL_MINUTES_IN_DAY;
    endDay = getPreviousDay(day);
  } else if (endWithOffset > TOTAL_MINUTES_IN_DAY) {
    endWithOffset -= TOTAL_MINUTES_IN_DAY;
    endDay = getNextDay(day);
  }

  // Handle the special case where start equals end (spans to next day)
  if (spansToNextDay) {
    const nextDay = getNextDay(startDay);
    return [
      { day: startDay, start: startWithOffset, end: TOTAL_MINUTES_IN_DAY },
      { day: nextDay, start: 0, end: endWithOffset }
    ];
  }

  if (crossesMidnight && offset < 0) {
    if (startDay === endDay) {
      return [{ day: startDay, start: startWithOffset, end: endWithOffset }];
    }

    if (startDay === getNextDay(day) && endDay === startDay) {
      return [{ day: startDay, start: startWithOffset, end: endWithOffset }];
    }

    if (startDay === getNextDay(day)) {
      return [{ day: startDay, start: startWithOffset, end: endWithOffset }];
    }
  }

  if (startDay !== endDay) {
    return [
      { day: startDay, start: startWithOffset, end: TOTAL_MINUTES_IN_DAY },
      { day: endDay, start: 0, end: endWithOffset }
    ];
  }

  if (endWithOffset < startWithOffset) {
    return [
      { day: startDay, start: startWithOffset, end: TOTAL_MINUTES_IN_DAY },
      { day: getNextDay(startDay), start: 0, end: endWithOffset }
    ];
  }

  return [{ day: startDay, start: startWithOffset, end: endWithOffset }];
};
export function splitScheduleRangeByDayBoundary(schedule: TRecruitmentSchedule): TRecruitmentSchedule[] {
  const result: TRecruitmentSchedule[] = [];
  const { day_of_week: day, start_minutes: start, end_minutes: end } = schedule;

  // Special case: exact same time
  if (start === end) {
    return [{ day_of_week: day, start_minutes: start, end_minutes: end }];
  }

  // Normal case: start before end on same day
  if (start < end) {
    result.push({ day_of_week: day, start_minutes: start, end_minutes: end });
  } else {
    // Overnight case: start time is after end time
    result.push({ day_of_week: day, start_minutes: start, end_minutes: TOTAL_MINUTES_IN_DAY });
    result.push({ day_of_week: getNextDay(day), start_minutes: 0, end_minutes: end });
  }

  return result;
}

/**
 * From: https://nedbatchelder.com/blog/201310/range_overlap_in_two_compares.html
 *
 * "Instead of thinking about how overlapping ranges might relate, consider what
 * conditions have to be true for the ranges not to overlap. It's much simpler:
 * either range1 is entirely less than range2, or range1 is entirely greater
 * than range2."
 */
export function doRangesOverlap(start1: number, end1: number, start2: number, end2: number) {
  return end1 >= start2 && end2 >= start1;
}

export function mergeOverlappingPeriods(scheduleArray: TRecruitmentSchedule[]) {
  if (!scheduleArray.length) {
    return [];
  }

  // Group entries by day_of_week
  const entriesByDay = scheduleArray.reduce<{ [key: string]: TRecruitmentSchedule[] }>((acc, entry) => {
    if (!acc[entry.day_of_week]) {
      acc[entry.day_of_week] = [];
    }
    acc[entry.day_of_week].push(entry);
    return acc;
  }, {});

  const result: TRecruitmentSchedule[] = [];

  // Process each day separately
  Object.keys(entriesByDay).forEach((day) => {
    const dayEntries = entriesByDay[day].sort(
      (a: TRecruitmentSchedule, b: TRecruitmentSchedule) => a.start_minutes - b.start_minutes
    );
    let currentPeriod = { ...dayEntries[0] };

    for (let i = 1; i < dayEntries.length; i++) {
      const nextPeriod = dayEntries[i];
      if (nextPeriod.start_minutes <= currentPeriod.end_minutes) {
        currentPeriod.end_minutes = Math.max(currentPeriod.end_minutes, nextPeriod.end_minutes);
      } else {
        result.push({ ...currentPeriod });
        currentPeriod = { ...nextPeriod };
      }
    }
    result.push({ ...currentPeriod });
  });

  return result.sort((a, b) => a.day_of_week - b.day_of_week);
}

export const getDayNumbers = (days: RecruitmentScheduleDay): number[] => {
  if (days === RecruitmentScheduleDay.AnyDay) {
    return [0, 1, 2, 3, 4, 5, 6];
  } else if (days === RecruitmentScheduleDay.Weekdays) {
    return [1, 2, 3, 4, 5];
  } else if (days === RecruitmentScheduleDay.Weekends) {
    return [0, 6];
  }

  const day = safeParseIntOrDefault(days, undefined);

  if (typeof day === 'undefined') {
    return [];
  }

  return [day];
};

export function formatTimeDelta(startTime: number, endTime: number) {
  if (startTime === endTime) {
    return t('recruitment:schedule.any-time');
  }

  let minutes = 0;
  if (endTime < startTime) {
    minutes = TOTAL_MINUTES_IN_DAY - startTime + endTime;
  } else {
    minutes = endTime - startTime;
  }

  return formatMsLong(minutes * 60 * 1000);
}

export function adjustScheduleForUTCOffset(day: number, timeInMinutes: number, utcOffsetMinutes: number) {
  let newTime = Number(timeInMinutes) + utcOffsetMinutes;
  let newDay = day;

  if (newTime < 0) {
    newTime += 60 * 24;
    newDay -= 1;
    if (newDay < 0) {
      newDay = 6;
    }
  } else if (newTime >= 60 * 24) {
    newTime -= 60 * 24;
    newDay = (newDay + 1) % 7;
  }

  return { day: newDay, time: newTime };
}

export function adjustScheduleArrayForTimezone(scheduleArray: any[], utcOffset: number) {
  const newArray: any[] = [];
  for (const entry of scheduleArray) {
    const startAdjusted = adjustScheduleForUTCOffset(entry.day_of_week, entry.start_minutes, utcOffset);
    const endAdjusted = adjustScheduleForUTCOffset(entry.day_of_week, entry.end_minutes, utcOffset);
    newArray.push({
      day_of_week: startAdjusted.day,
      start_minutes: startAdjusted.time,
      end_minutes: endAdjusted.time
    });
  }
  return newArray;
}

export function adjustScheduleObjectForTimezone(scheduleObj: any, utcOffset: number) {
  const newScheduleObj: any = {};
  const scheduleObjAsArray = _.entries(scheduleObj).map(([dayString, times]: any) => [
    parseInt(dayString, 10),
    times.gte,
    times.lte
  ]);

  for (const [day, start, end] of scheduleObjAsArray) {
    const startAdjusted = adjustScheduleForUTCOffset(day, start, utcOffset);
    const endAdjusted = adjustScheduleForUTCOffset(day, end, utcOffset);
    newScheduleObj[startAdjusted.day] = { gte: startAdjusted.time, lte: endAdjusted.time };
  }

  return newScheduleObj;
}

export type TRecruitmentSchedule = {
  day_of_week: number;
  start_minutes: number;
  end_minutes: number;
};

export const ACTIVITY_OPTS = [
  { label: 'Raiding', value: RecruitmentActivityType.GuildRaids },
  { label: 'Guild Mythic+', value: RecruitmentActivityType.GuildMythicPlus },
  { label: 'Guild PVP', value: RecruitmentActivityType.GuildPvp },
  { label: 'Guild Social', value: RecruitmentActivityType.GuildSocial },
  { label: 'Team Mythic+', value: RecruitmentActivityType.TeamMythicPlus }
];

export const TITLE_FOR_ENTITY: Record<RecruitmentEntityType, string> = {
  [RecruitmentEntityType.Character]: 'Character',
  [RecruitmentEntityType.Guild]: 'Guild',
  [RecruitmentEntityType.Team]: 'Mythic+ Team'
};
